import React, { useEffect, useState } from 'react'
import { ActivityIndicator, Image, TouchableOpacity, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'

import { useStores } from '@trivie/core'
import { color, Header, iconArrowBack, iconCheckCircle, SvgIcon, Text } from '@trivie/ui-mobile'
import { useHistory, useRouteMatch } from 'react-router-dom'

import trivieDefault from '../play-screen/default-backdrop.png'
import contestNull from './contest-null.png'

export interface ContestScreenProps {}

export const ContestInfoScreen: React.FunctionComponent<ContestScreenProps> = observer((props) => {
  const { rootCache, contestStore, libraryStore, userStore } = useStores()
  const history = useHistory()

  const { user } = userStore
  const { getTopicsForUser, getQuizzesForUser, topics, quizzes } = libraryStore
  const { howToWin, contestsForUser, getHowToWin } = contestStore

  const { params } = useRouteMatch() as any
  const { contestID } = params

  const [contest, setContest] = useState<any>()

  useEffect(() => {
    if (contestID) {
      const found = contestsForUser.find((c) => Number(c.id) === Number(contestID))
      if (found) {
        setContest(found)
      }
    }
  }, [])

  const [loading, setLoading] = useState(true)
  const [scoringType, setScoringType] = useState<any>('')
  const [content, setContent] = useState<any>([])
  const [allEligible, setAllEligible] = useState(false)
  const [noneEligible, setNoneEligible] = useState(false)

  const { t } = useTranslation()

  const setScoringMessage = (data) => {
    let type = ''
    if (user?.workspace_settings?.feed_enabled) {
      const answers = data.find((scoringMethod) => scoringMethod.item === 'answers')
      const reinforcements = data.find((scoringMethod) => scoringMethod.item === 'reinforcements')
      const tests = data.find((scoringMethod) => scoringMethod.item === 'tests')
      const posts = data.find((scoringMethod) => scoringMethod.item === 'posts')
      const comments = data.find((scoringMethod) => scoringMethod.item === 'comments')

      const showPosts = posts?.enabled && posts?.points_awarded
      const showReplies = comments?.enabled && comments?.points_awarded
      const showQuestions =
        (tests?.enabled && tests?.points_awarded > 0) ||
        (answers?.enabled && answers?.points_awarded) > 0 ||
        (reinforcements?.enabled && reinforcements?.points_awarded > 0)

      if (showQuestions && showPosts && showReplies) {
        type = t('Contests.scoringMessage1')
      } else if (showQuestions && showPosts && !showReplies) {
        type = t('Contests.scoringMessage2')
      } else if (showQuestions && showReplies && !showPosts) {
        type = t('Contests.scoringMessage3')
      } else if (showReplies && !showPosts && !showQuestions) {
        type = t('Contests.scoringMessage4')
      } else if (showReplies && showPosts && !showQuestions) {
        type = t('Contests.scoringMessage5')
      } else if (showPosts && !showReplies && !showQuestions) {
        type = t('Library.Posts')
      } else if (showQuestions) {
        type = t('Quiz.questionsLabel')
      }
    }

    setScoringType(type)
  }

  const load = async (id) => {
    setLoading(true)

    getHowToWin(id).then((data) => {
      const c = [] as any

      data.content.map((item) => {
        if (item?.quiz_id === null) {
          const topic = topics.find((tpc: any) => tpc?.topic.topic_id === item?.topic_id)
          if (topic) {
            c.push(topic)
          }
        } else {
          const quiz = quizzes.find((q: any) => q?.quiz_id === item?.quiz_id)
          if (quiz) {
            c.push(quiz)
          }
        }
      })

      if (c.length === 0) {
        setNoneEligible(true)
        setAllEligible(false)
      }

      if (data.content.length === 0) {
        setAllEligible(true)
        setNoneEligible(false)
      }

      setScoringMessage(data.scoring_overview)

      setContent(c)
      setLoading(false)
    })
  }

  useEffect(() => {
    setLoading(true)
    if (user) {
      rootCache.invalidateAction('getTopicsForUser', libraryStore)
      rootCache.invalidateAction('getQuizzesForUser', libraryStore)
      Promise.all([getTopicsForUser(user.id), getQuizzesForUser(user.id)]).then(() => {
        load(contestID)
      })
    }
  }, [])

  const goToTopic = (topicID: number | null, assignmentID: number, lockedByPreTest: boolean) => {
    history.push(`/library/${assignmentID}/${topicID}`)
  }

  const goToQuiz = (assignmentID: number, topicID: number, quizID: number) => {
    history.push(`/quiz/${assignmentID}/${topicID}/${quizID}`)
  }

  const getTitle = (item: string) => {
    switch (item) {
      case 'answers':
        return 'Contests.regularAnswers'
      case 'comments':
        return 'Contests.newReplies'
      case 'surveys':
        return 'Contests.surveyAnswers'
      case 'posts':
        return 'Contests.newPosts'
      case 'badges':
        return 'Contests.newBadges'
      case 'reinforcements':
        return 'Contests.reinforcementAnswers'
      case 'tests':
        return 'Contests.testAnswers'
      default:
        return ''
    }
  }

  const getSubtitle = (item: string) => {
    switch (item) {
      case 'answers':
        return 'Contests.answerSubtitle'
      case 'comments':
        return 'Contests.repliesSubtitle'
      case 'surveys':
        return 'Contests.surveysSubtitle'
      case 'posts':
        return 'Contests.postsSubtitle'
      case 'badges':
        return 'Contests.badgesSubtitle'
      case 'reinforcements':
        return 'Contests.reinforcementSubtitle'
      case 'tests':
        return 'Contests.testsSubtitle'
      default:
        return ''
    }
  }

  return (
    <View style={FULL}>
      <View style={{ ...CONTAINER, height: '100%' }}>
        <div style={HEADER_STICKY_CONTAINER}>
          <div style={HEADER_FLEX_CONTAINER}>
            <div style={HEADER_ITEM}>
              <Header
                leftIcon={iconArrowBack}
                onLeftPress={history.goBack}
                center
                headerTx="Contests.howToWin"
              />
            </div>
          </div>
        </div>
        {loading ? (
          <View
            style={{ height: '100%', flex: 1, justifyContent: 'center', flexDirection: 'column' }}
          >
            <ActivityIndicator />
          </View>
        ) : (
          <ScrollView>
            <>
              <Text
                tx="Contests.scoring"
                variant="titleEmphasized"
                style={{ marginBottom: 20, alignSelf: 'center' }}
              />
              <Text
                tx="Contests.earnPoints"
                txOptions={{ title: contest?.title }}
                variant="title"
                style={{ marginBottom: 22, alignSelf: 'center' }}
              />
              <View style={{ marginBottom: 24 }}>
                {howToWin?.scoring_overview.map((scoring) => {
                  return scoring?.enabled && scoring?.points_awarded > 0 ? (
                    <div style={SCORING_ROW}>
                      <div style={{ flexDirection: 'row', display: 'flex' }}>
                        <SvgIcon src={iconCheckCircle} color={color.green} size={24} />
                        <div style={{ flexDirection: 'column', justifyContent: 'center' }}>
                          <Text
                            variant="body1"
                            style={{ marginLeft: 14 }}
                            tx={getTitle(scoring.item)}
                          />
                          <Text
                            style={{ marginLeft: 14, color: color.shade70 }}
                            variant="body2"
                            tx={getSubtitle(scoring.item)}
                          />
                        </div>
                      </div>
                      <Text
                        style={{ color: color.shade50 }}
                        variant="body1"
                        text={`+${scoring?.points_awarded}`}
                      />
                    </div>
                  ) : null
                })}
              </View>
              <Text
                tx="Contests.eligible"
                variant="titleEmphasized"
                style={{ marginBottom: 20, alignSelf: 'center' }}
              />
              <Text
                tx={
                  allEligible
                    ? 'Contests.eligibleAll'
                    : noneEligible
                      ? 'Contests.eligibleNone'
                      : 'Contests.eligibleSome'
                }
                txOptions={{ type: scoringType }}
                variant="title"
                style={{ marginBottom: 22, alignSelf: 'center' }}
              />
              {content.map((item) => {
                // topic
                if (!item?.quiz_id) {
                  return (
                    <TouchableOpacity
                      onPress={() =>
                        goToTopic(
                          item?.topic?.topic_id,
                          item?.assignment_id,
                          item?.locked_by_pre_test,
                        )
                      }
                      style={{ alignItems: 'center', flexDirection: 'row', marginBottom: 12 }}
                    >
                      <Image
                        source={{ uri: item?.topic?.photo }}
                        style={{ height: 32, width: 40, borderRadius: 6, marginRight: 12 }}
                      />
                      <View style={{ flexDirection: 'column' }}>
                        <Text text={item?.topic?.title} variant="body2Emphasized" />
                        <Text
                          txOptions={{ count: item?.topic?.quiz_count }}
                          tx="Topic.count"
                          style={{ color: color.shade50, textTransform: 'lowercase' }}
                          variant="body2Emphasized"
                        />
                      </View>
                    </TouchableOpacity>
                  )
                } else {
                  return (
                    <TouchableOpacity
                      onPress={() =>
                        goToQuiz(item?.assignment_id, item?.topic?.topic_id, item?.quiz_id)
                      }
                      style={{ alignItems: 'center', flexDirection: 'row', marginBottom: 12 }}
                    >
                      <img
                        src={trivieDefault}
                        style={{ height: 32, width: 40, borderRadius: 6, marginRight: 12 }}
                      />
                      <View style={{ flexDirection: 'column' }}>
                        <Text text={item?.title} variant="body2Emphasized" />
                      </View>
                    </TouchableOpacity>
                  )
                }
              })}
              {noneEligible && (
                <img
                  style={{ height: 198, width: 300, alignSelf: 'center', marginTop: 32 }}
                  src={contestNull}
                />
              )}
            </>
          </ScrollView>
        )}
      </View>
    </View>
  )
})

/**
 * Styling
 */
const FULL: ViewStyle = { flex: 1, height: '100%' }
const CONTAINER: ViewStyle = { backgroundColor: color.white }
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER_ITEM: React.CSSProperties = { width: '100%' }
const SCORING_ROW: React.CSSProperties = {
  marginBottom: 10,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}
